import React, { useMemo, useState } from "react";
import { ArrowForward, WarningAmberOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Divider, MenuItem, Select, Tooltip, Typography } from "@mui/material";

import { LayerStatus } from "fond/hooks/useGetImportLayers";
import { MuPowerInputLayerId } from "fond/types";

import type { BulkUploadGroup } from "./bulkUpload";
import { muPowerInputLayers } from "./layers";

interface BulkUploadReviewProps {
  groups: BulkUploadGroup[];
  inputLayers: Map<string, LayerStatus>;
  onCancel: () => void;
  onConfirm: (selections: Array<{ layerId: MuPowerInputLayerId; files: File[] }>) => void;
}

const IGNORE_VALUE = "__ignore__";

type Selection = MuPowerInputLayerId | typeof IGNORE_VALUE;

const BulkUploadReview: React.FC<BulkUploadReviewProps> = ({ groups, inputLayers, onCancel, onConfirm }) => {
  const [selections, setSelections] = useState<Selection[]>(() => groups.map((g) => g.matchedLayerId ?? IGNORE_VALUE));

  const layerOptions = useMemo(() => muPowerInputLayers.map(({ key, displayName }) => ({ value: key, label: displayName })), []);

  const assignedLayerIds = useMemo(() => {
    const counts = new Map<Selection, number>();
    selections.forEach((sel) => counts.set(sel, (counts.get(sel) ?? 0) + 1));
    return counts;
  }, [selections]);

  const hasDuplicateAssignment = Array.from(assignedLayerIds.entries()).some(([sel, count]) => sel !== IGNORE_VALUE && count > 1);

  const hasAnyUpload = selections.some((sel, i) => sel !== IGNORE_VALUE && !groups[i].validationError);

  const handleSelectionChange = (index: number, value: Selection) => {
    setSelections((prev) => prev.map((v, i) => (i === index ? value : v)));
  };

  const handleConfirm = () => {
    const payload: Array<{ layerId: MuPowerInputLayerId; files: File[] }> = [];
    selections.forEach((sel, i) => {
      if (sel !== IGNORE_VALUE && !groups[i].validationError) {
        payload.push({ layerId: sel, files: groups[i].files });
      }
    });
    onConfirm(payload);
  };

  return (
    <Box data-testid="mu-power-bulk-upload-review">
      <Typography fontWeight={500} mb={1}>
        Review file matches
      </Typography>

      <Box>
        {groups.map((group, index) => {
          const selection = selections[index];
          const layerStatus = selection !== IGNORE_VALUE ? inputLayers.get(selection) : undefined;
          const willOverwrite = !!layerStatus?.layer && selection !== IGNORE_VALUE;

          return (
            <Box key={group.baseName} data-testid={`bulk-upload-row-${group.baseName}`} sx={{ py: 0.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title={group.validationError ?? group.baseName}>
                  <Typography variant="body2" noWrap sx={{ width: 100, flexShrink: 0, color: group.validationError ? "error.main" : undefined }}>
                    {group.baseName}
                  </Typography>
                </Tooltip>
                <ArrowForward sx={{ fontSize: 14, color: "text.disabled" }} />
                <Select
                  size="small"
                  value={selection}
                  onChange={(e) => handleSelectionChange(index, e.target.value as Selection)}
                  disabled={!!group.validationError}
                  data-testid={`bulk-upload-select-${group.baseName}`}
                  sx={{ flex: 1, "& .MuiSelect-select": { py: 0.5 } }}
                >
                  <MenuItem value={IGNORE_VALUE}>Don&apos;t upload</MenuItem>
                  {layerOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                <Box sx={{ width: 20, display: "flex", justifyContent: "center" }}>
                  {willOverwrite && (
                    <Tooltip title="This layer already has data. Uploading will replace it.">
                      <WarningAmberOutlined color="warning" fontSize="small" />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {hasDuplicateAssignment && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Two file groups are assigned to the same layer. Please pick only one.
        </Alert>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Box display="flex" justifyContent="flex-end" gap={1}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!hasAnyUpload || hasDuplicateAssignment}
          onClick={handleConfirm}
          data-testid="bulk-upload-confirm"
        >
          Upload all
        </Button>
      </Box>
    </Box>
  );
};

export default BulkUploadReview;
